/**
 * ConfirmBeforeConnectDialog — save-guard modal shown before the picker
 * triggers a full-page Stripe OAuth redirect.
 *
 * Behavior (per plan §A.6):
 *   - `hasUnsavedChanges === true`  → 3 buttons: Save+Connect, Discard+Connect, Cancel
 *   - `hasUnsavedChanges === false` → 2 buttons: Continue+Connect, Cancel
 *   - Save errors during onSaveAndConnect surface inline; modal stays open.
 *
 * The modal is a thin shell — all the OAuth side-effects (sessionStorage
 * write + window.location.href redirect) live in the parent picker's
 * handler. This component only exposes intent: "user wants to save before
 * connect", "user wants to discard", "user wants to cancel".
 */

import React, { useCallback, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
} from '@mui/material';
import { EDITOR } from '@test-kit/selectors/editor';

export interface ConfirmBeforeConnectDialogProps {
  open: boolean;
  /** Stripe mode that triggered the prompt — drives display copy. */
  mode: 'test' | 'live';
  /**
   * When true, the modal shows 3 buttons (Save+Connect, Discard+Connect,
   * Cancel). When false, it shows 2 (Continue+Connect, Cancel).
   */
  hasUnsavedChanges: boolean;
  /**
   * Awaited when the user picks "Save and connect". On resolve, the dialog
   * closes and `onProceed` fires. On reject, an Alert is shown inside the
   * dialog and the modal stays open — `onProceed` is NOT called.
   */
  onSaveAndConnect: () => Promise<void>;
  /**
   * Called for the "Discard and connect" / "Continue and connect" paths.
   * The dialog closes synchronously and the parent does its redirect.
   */
  onProceed: () => void;
  /** User chose Cancel — modal closes, no redirect. */
  onCancel: () => void;
}

const MODE_LABEL: Record<'test' | 'live', string> = {
  test: 'Sandbox',
  live: 'Live',
};

export const ConfirmBeforeConnectDialog: React.FC<ConfirmBeforeConnectDialogProps> = ({
  open,
  mode,
  hasUnsavedChanges,
  onSaveAndConnect,
  onProceed,
  onCancel,
}) => {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const label = MODE_LABEL[mode];

  /**
   * Handle "Save and connect" — catch any rejection from onSaveAndConnect
   * and render it inline. Only fire onProceed on a clean save.
   */
  const handleSaveAndConnect = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await onSaveAndConnect();
      // Caller resolved → screen saved. Hand off to redirect.
      onProceed();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save changes';
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  }, [onSaveAndConnect, onProceed]);

  /** "Discard and connect" / "Continue and connect" — straight to redirect. */
  const handleDiscardAndConnect = useCallback(() => {
    onProceed();
  }, [onProceed]);

  const handleCancel = useCallback(() => {
    setSaveError(null);
    onCancel();
  }, [onCancel]);

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="xs"
      fullWidth
      data-testid={EDITOR.paywallProductPicker.connectConfirmDialog}
    >
      <DialogTitle>{`Connect ${label} Stripe Account`}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: saveError ? 1 : 0 }}>
          {hasUnsavedChanges
            ? `You'll be redirected to Stripe to authorize the ${label} account. ` +
              'You have unsaved changes — save them first, or discard and continue.'
            : `You'll be redirected to Stripe to authorize the ${label} account. ` +
              "When you return, you'll come back to this paywall."}
        </DialogContentText>
        {saveError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {`Couldn't save: ${saveError}`}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCancel}
          disabled={saving}
          data-testid={EDITOR.paywallProductPicker.connectConfirmCancel}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        {hasUnsavedChanges ? (
          <>
            <Button
              onClick={handleDiscardAndConnect}
              disabled={saving}
              color="warning"
              data-testid={EDITOR.paywallProductPicker.connectConfirmDiscard}
              sx={{ textTransform: 'none' }}
            >
              Discard and connect
            </Button>
            <Button
              onClick={handleSaveAndConnect}
              disabled={saving}
              variant="contained"
              data-testid={EDITOR.paywallProductPicker.connectConfirmSave}
              sx={{ textTransform: 'none' }}
            >
              {saving ? 'Saving…' : 'Save and connect'}
            </Button>
          </>
        ) : (
          <Button
            onClick={handleDiscardAndConnect}
            disabled={saving}
            variant="contained"
            data-testid={EDITOR.paywallProductPicker.connectConfirmDiscard}
            sx={{ textTransform: 'none' }}
          >
            Continue and connect
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

ConfirmBeforeConnectDialog.displayName = 'ConfirmBeforeConnectDialog';
